<template>
  <div class="app-version-config" v-loading="loading">
    <div class="page-header">
      <h2>App 版本管理</h2>
      <p class="page-desc">配置各平台的最低版本和最新版本，控制强制更新策略</p>
    </div>

    <div class="platform-cards">
      <el-card v-for="platform in platforms" :key="platform" class="platform-card" shadow="hover">
        <div slot="header" class="card-header">
          <span class="platform-name">
            <i :class="platform === 'android' ? 'el-icon-mobile-phone' : 'el-icon-mobile'"></i>
            {{ platform === 'android' ? 'Android' : 'iOS' }}
          </span>
          <el-switch
            v-model="configs[platform].is_enabled"
            active-text="启用"
            inactive-text="关闭"
            @change="handleSave(platform)"
          />
        </div>

        <el-form :model="configs[platform]" label-width="120px" size="small">
          <el-form-item label="最低版本">
            <el-input v-model="configs[platform].min_version" placeholder="如 1.0.0">
              <template slot="prepend">v</template>
            </el-input>
            <div class="form-tip">低于此版本的用户将被强制更新，无法继续使用</div>
          </el-form-item>

          <el-form-item label="最新版本">
            <el-input v-model="configs[platform].latest_version" placeholder="如 1.2.0">
              <template slot="prepend">v</template>
            </el-input>
            <div class="form-tip">高于用户当前版本时，弹出可选更新提示</div>
          </el-form-item>

          <el-form-item label="下载地址">
            <el-input v-model="configs[platform].update_url" placeholder="应用商店链接或 APK 下载地址" />
          </el-form-item>

          <el-form-item label="更新说明">
            <el-input
              v-model="configs[platform].update_message"
              type="textarea"
              :rows="3"
              placeholder="本次更新内容说明，将展示给用户"
            />
          </el-form-item>

          <el-form-item>
            <el-button type="primary" @click="handleSave(platform)" :loading="saving[platform]">
              保存配置
            </el-button>
          </el-form-item>
        </el-form>

        <div v-if="configs[platform].updated_at" class="last-update">
          最后更新: {{ formatTime(configs[platform].updated_at) }}
        </div>
      </el-card>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, reactive, ref, onMounted } from 'vue'
import { getAppVersions, upsertAppVersion, AppVersionConfig } from '@/api/appVersion'
import { Message } from 'element-ui'

const defaultConfig = (platform: string): AppVersionConfig => ({
  platform,
  min_version: '1.0.0',
  latest_version: '1.0.0',
  update_url: '',
  update_message: '',
  is_enabled: true,
})

export default defineComponent({
  name: 'AppVersionConfig',
  setup() {
    const loading = ref(false)
    const platforms = ['android', 'ios']
    const configs = reactive<Record<string, AppVersionConfig>>({
      android: defaultConfig('android'),
      ios: defaultConfig('ios'),
    })
    const saving = reactive<Record<string, boolean>>({
      android: false,
      ios: false,
    })

    const fetchConfigs = async () => {
      loading.value = true
      try {
        const list = await getAppVersions()
        for (const item of list) {
          if (item.platform in configs) {
            Object.assign(configs[item.platform], item)
          }
        }
      } catch (e) {
        console.error('获取版本配置失败', e)
      } finally {
        loading.value = false
      }
    }

    const handleSave = async (platform: string) => {
      const config = configs[platform]

      // 校验版本号格式
      const versionRegex = /^\d+\.\d+\.\d+$/
      if (!versionRegex.test(config.min_version)) {
        Message.error('最低版本号格式不正确，应为 x.y.z 格式')
        return
      }
      if (!versionRegex.test(config.latest_version)) {
        Message.error('最新版本号格式不正确，应为 x.y.z 格式')
        return
      }

      saving[platform] = true
      try {
        const result = await upsertAppVersion({
          platform: config.platform,
          min_version: config.min_version,
          latest_version: config.latest_version,
          update_url: config.update_url,
          update_message: config.update_message,
          is_enabled: config.is_enabled,
        })
        Object.assign(configs[platform], result)
        Message.success(`${platform === 'android' ? 'Android' : 'iOS'} 配置已保存`)
      } catch (e) {
        console.error('保存版本配置失败', e)
      } finally {
        saving[platform] = false
      }
    }

    const formatTime = (time: string | undefined) => {
      if (!time) return ''
      return new Date(time).toLocaleString('zh-CN')
    }

    onMounted(fetchConfigs)

    return {
      loading,
      platforms,
      configs,
      saving,
      handleSave,
      formatTime,
    }
  },
})
</script>

<style scoped lang="scss">
.app-version-config {
  max-width: 900px;
}

.page-header {
  margin-bottom: 24px;

  h2 {
    font-size: 22px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 8px;
  }

  .page-desc {
    font-size: 14px;
    color: #909399;
  }
}

.platform-cards {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.platform-card {
  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .platform-name {
    font-size: 16px;
    font-weight: 600;

    i {
      margin-right: 8px;
      color: #6366f1;
    }
  }
}

.form-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
  line-height: 1.4;
}

.last-update {
  font-size: 12px;
  color: #c0c4cc;
  text-align: right;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
}
</style>
